import React, { forwardRef } from "react";
import { TICKET_PRICE, TOTAL_TICKETS, formatUSD } from "../utils/formatters";

const formatDateTime = (d = new Date()) =>
  d.toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" });

// Hidden by default; @media print rules in index.css reveal it.
const PrintFrame = forwardRef(({ title, year, report, children }, ref) => (
  <div ref={ref} className="print-area" data-report={report} aria-hidden="true">
    <div className="print-header">
      <h1 className="print-title">{title}</h1>
      <p className="print-subtitle">
        Red Hook Rhinebeck Elks Lodge #2022 · Four Hundred Club · Raffle Year {year}
      </p>
      <p className="print-date">Generated {formatDateTime()}</p>
    </div>
    {children}
  </div>
));
PrintFrame.displayName = "PrintFrame";

export const SellerActivityReport = ({ tickets, sellers, year }) => {
  // Per-seller: assigned count, sold count (sold + funds_received), funds_received count, $ collected
  const rows = [];
  const byName = {};
  const allSellerNames = new Set();

  for (let i = 1; i <= TOTAL_TICKETS; i++) {
    const t = tickets[i];
    if (!t || !t.assigned_seller_name) continue;
    const name = t.assigned_seller_name;
    allSellerNames.add(name);
    byName[name] = byName[name] || { assigned: 0, sold: 0, funds: 0, collected: 0 };
    byName[name].assigned += 1;
    if (t.status === "sold" || t.status === "funds_received") byName[name].sold += 1;
    if (t.status === "funds_received") {
      byName[name].funds += 1;
      byName[name].collected += Number(t.tender_amount || TICKET_PRICE);
    }
  }
  // Include sellers with assignments but no activity yet
  (sellers || []).forEach((s) => allSellerNames.add(s.name));

  for (const name of Array.from(allSellerNames).sort()) {
    const r = byName[name] || { assigned: 0, sold: 0, funds: 0, collected: 0 };
    rows.push({ name, ...r });
  }

  // Totals
  const totals = rows.reduce(
    (acc, r) => ({
      assigned: acc.assigned + r.assigned,
      sold: acc.sold + r.sold,
      funds: acc.funds + r.funds,
      collected: acc.collected + r.collected,
    }),
    { assigned: 0, sold: 0, funds: 0, collected: 0 }
  );

  return (
    <PrintFrame title="Seller Activity Report" year={year} report="seller-activity">
      <table className="print-table">
        <thead>
          <tr>
            <th className="num">#</th>
            <th>Seller name</th>
            <th className="num">Tickets assigned</th>
            <th className="num">Tickets sold</th>
            <th className="num">Funds turned in</th>
            <th className="num">$ collected</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={6} className="empty">No seller activity yet.</td></tr>
          ) : rows.map((r, i) => (
            <tr key={r.name}>
              <td className="num">{i + 1}</td>
              <td>{r.name}</td>
              <td className="num">{r.assigned}</td>
              <td className="num">{r.sold}</td>
              <td className="num">{r.funds}</td>
              <td className="num">{formatUSD(r.collected)}</td>
            </tr>
          ))}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr>
              <td colSpan={2} className="totals-label">TOTAL</td>
              <td className="num totals">{totals.assigned}</td>
              <td className="num totals">{totals.sold}</td>
              <td className="num totals">{totals.funds}</td>
              <td className="num totals">{formatUSD(totals.collected)}</td>
            </tr>
          </tfoot>
        )}
      </table>
      <p className="print-footer">
        End of report · {rows.length} seller{rows.length === 1 ? "" : "s"} listed.
      </p>
    </PrintFrame>
  );
};

export const SoldTicketsReport = ({ tickets, year }) => {
  // All tickets with status funds_received (turned in), sorted by ticket number
  const rows = [];
  for (let i = 1; i <= TOTAL_TICKETS; i++) {
    const t = tickets[i];
    if (!t || t.status !== "funds_received") continue;
    rows.push({
      n: i,
      buyer: t.buyer || "—",
      phone: t.phone || "—",
      tender_type: t.tender_type || "",
      check_number: t.check_number || "",
      tender_amount: Number(t.tender_amount || 0),
      seller: t.assigned_seller_name || "—",
    });
  }
  const total = rows.reduce((s, r) => s + r.tender_amount, 0);

  const renderTender = (r) => {
    if (!r.tender_type) return <span className="not-recorded">Not recorded</span>;
    if (r.tender_type === "check") {
      return <span>Check{r.check_number ? ` #${r.check_number}` : ""}</span>;
    }
    return <span style={{ textTransform: "capitalize" }}>{r.tender_type}</span>;
  };

  return (
    <PrintFrame title="Tickets Turned In Report" year={year} report="sold-tickets">
      <table className="print-table">
        <thead>
          <tr>
            <th className="num">Ticket #</th>
            <th>Buyer name</th>
            <th>Phone</th>
            <th>Tender</th>
            <th className="num">Amount</th>
            <th>Seller</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={6} className="empty">No tickets turned in yet.</td></tr>
          ) : rows.map((r) => (
            <tr key={r.n}>
              <td className="num">{r.n}</td>
              <td>{r.buyer}</td>
              <td>{r.phone}</td>
              <td>{renderTender(r)}</td>
              <td className="num">{r.tender_amount > 0 ? formatUSD(r.tender_amount) : "—"}</td>
              <td>{r.seller}</td>
            </tr>
          ))}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr>
              <td colSpan={4} className="totals-label">TOTAL · {rows.length} ticket{rows.length === 1 ? "" : "s"}</td>
              <td className="num totals">{formatUSD(total)}</td>
              <td></td>
            </tr>
          </tfoot>
        )}
      </table>
      <p className="print-footer">
        End of report · {rows.length} ticket{rows.length === 1 ? "" : "s"} listed.
      </p>
    </PrintFrame>
  );
};

/**
 * Triggers the browser's print dialog. The body gets a class that switches print CSS
 * to reveal only the chosen report and hide all other UI.
 */
export const runPrint = (mode) => {
  // mode = "seller-activity" | "sold-tickets"
  document.body.classList.add("printing", `printing-${mode}`);
  // Slight delay lets React commit the rendered report DOM (state-driven) before print fires.
  setTimeout(() => {
    window.print();
    // cleanup after the user closes the print dialog (afterprint fires on cancel too)
    const cleanup = () => {
      document.body.classList.remove("printing", `printing-${mode}`);
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
  }, 80);
};
